interface LayerDrag {
  pointerId: number;
  ids: readonly string[];
  x: number;
  y: number;
  startX: number;
  startY: number;
  before: string | null;
  moved: boolean;
}

/** List-only insertion preview. Scene order changes once, on a completed drop. */
export class NativeLayerDrag {
  private drag: LayerDrag | null = null;
  private frame = 0;
  private suppressClick = false;
  constructor(
    private list: HTMLElement,
    private scroller: HTMLElement,
    private start: (id: string, group: boolean) => readonly string[] | null,
    private drop: (ids: readonly string[], before: string | null) => void,
    private changed: () => void,
  ) {
    list.addEventListener("pointerdown", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest(".layer-actions, [data-layer-action], input")) return;
      const row = target.closest<HTMLElement>(
          "[data-layer-id], [data-group-id]",
        ),
        id = row?.dataset.layerId ?? row?.dataset.groupId;
      if (event.button !== 0 || !id || this.drag) return;
      const ids = start(id, !!row?.dataset.groupId);
      if (!ids?.length) return;
      this.drag = {
        ids,
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        startX: event.clientX,
        startY: event.clientY,
        before: null,
        moved: false,
      };
      changed();
    });
    window.addEventListener("pointermove", (event) => {
      const drag = this.drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      drag.x = event.clientX;
      drag.y = event.clientY;
      if (
        !drag.moved &&
        Math.hypot(drag.x - drag.startX, drag.y - drag.startY) < 5
      )
        return;
      if (!drag.moved) list.setPointerCapture(event.pointerId);
      drag.moved = true;
      this.suppressClick = true;
      event.preventDefault();
      this.preview();
      if (!this.frame) this.frame = requestAnimationFrame(() => this.scroll());
    });
    window.addEventListener("pointerup", (event) => {
      const drag = this.drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      drag.x = event.clientX;
      drag.y = event.clientY;
      this.preview();
      const valid = drag.moved && this.inside();
      this.cancel();
      if (valid) drop(drag.ids, drag.before);
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    });
    list.addEventListener(
      "click",
      (event) => {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.suppressClick = false;
        }
      },
      true,
    );
    list.addEventListener("pointercancel", () => this.cancel());
    list.addEventListener("lostpointercapture", (event) => {
      if (event.target === list) this.cancel();
    });
    window.addEventListener("blur", () => this.cancel());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.cancel();
    });
  }
  get active() {
    return this.drag !== null;
  }
  cancel() {
    const drag = this.drag;
    this.drag = null;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    for (const row of this.list.children) row.removeAttribute("data-drop");
    this.list.classList.remove("is-dragging");
    if (drag && this.list.hasPointerCapture(drag.pointerId))
      this.list.releasePointerCapture(drag.pointerId);
    if (drag) this.changed();
  }
  private inside() {
    const b = this.scroller.getBoundingClientRect(),
      drag = this.drag!;
    return (
      drag.x >= b.left &&
      drag.x <= b.right &&
      drag.y >= b.top &&
      drag.y <= b.bottom
    );
  }
  private preview() {
    const drag = this.drag!;
    for (const row of this.list.children) row.removeAttribute("data-drop");
    this.list.classList.toggle("is-dragging", drag.moved);
    if (!drag.moved || !this.inside()) return;
    const moving = new Set(drag.ids);
    const rowId = (row: HTMLElement) =>
      row.dataset.layerId ?? row.dataset.groupFirstLayerId;
    const rows = Array.from(
      this.list.querySelectorAll<HTMLElement>(
        "[data-layer-id], [data-group-first-layer-id]",
      ),
    ).filter((row) => !row.hidden && !moving.has(rowId(row)!));
    const next = rows.find((row) => {
      const box = row.getBoundingClientRect();
      return drag.y < box.y + box.height / 2;
    });
    drag.before = next ? rowId(next)! : null;
    if (next) next.dataset.drop = "before";
    else if (rows.length) rows[rows.length - 1]!.dataset.drop = "after";
  }
  private scroll() {
    this.frame = 0;
    if (!this.drag?.moved) return;
    const b = this.scroller.getBoundingClientRect(),
      y = this.drag.y;
    const speed =
      y < b.top + 32
        ? Math.max(-12, (y - b.top - 32) / 3)
        : y > b.bottom - 32
          ? Math.min(12, (y - b.bottom + 32) / 3)
          : 0;
    if (this.drag.x >= b.left && this.drag.x <= b.right && speed) {
      const before = this.scroller.scrollTop;
      this.scroller.scrollTop += speed;
      this.preview();
      if (this.scroller.scrollTop !== before)
        this.frame = requestAnimationFrame(() => this.scroll());
    }
  }
}
